import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ScrollView, StyleSheet, Text, TextInput, View } from 'react-native'
import Animated, {
  Easing,
  LinearTransition,
  useAnimatedProps,
  useSharedValue,
  withTiming
} from 'react-native-reanimated'
import { useQuery } from 'convex/react'

import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'

import { DS } from '@/design-system'
import { toastNotificationStore } from '@/components/ANewDesign/ToastNotification/ToastNotification'
import { useStore } from '@/models'
import { events } from '@/models/analytics/analytics-events'
import { PlayerAvatar } from '@/components/ANewDesign/PlayerAvatar'
import { buildPlayerColorMap } from '../../../utils/playerColors'
import { fontFamily } from '../../../theme'
import type { DSColor } from '@ppwr/poker'
import type { LeaderboardPlayer } from '@/components/ANewDesign/LeaderboardList/LeaderboardList'
import { useMyTournamentTable } from '../useMyTournamentTable'

const DEFAULT_RUN_TIME = '00:00:00'

function formatRunTime(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${pad(h)}:${pad(m)}:${pad(s)}`
}

function getRunTime(
  status: { state: string; startedAt: number; finishedAt?: number | null } | null | undefined,
  now: number
): string {
  if (!status) return DEFAULT_RUN_TIME
  if (status.state === 'registration') return DEFAULT_RUN_TIME
  // Cap at the finish time once the tournament is over — otherwise the clock
  // keeps counting forever every time someone reopens the lobby. Tournaments
  // finished before `finishedAt` existed have it null; those keep ticking
  // (legacy behavior) until backfilled.
  const end = status.state === 'finished' && status.finishedAt ? status.finishedAt : now
  const seconds = Math.max(0, Math.round((end - status.startedAt) / 1000))
  return formatRunTime(seconds)
}

type TournamentStatus = NonNullable<ReturnType<typeof useQuery<typeof api.tournaments.queries.getTournamentStatus>>>

interface Props {
  tournamentId: Id<'tournaments'>
  /** Set when the user just got eliminated; surface a toast on mount. */
  justEliminated?: boolean
  /** Set when the tournament just finished; surface a winner toast on mount. */
  justFinished?: boolean
  /**
   * Optional pre-loaded status from the parent lobby. When provided, this
   * view skips its own `getTournamentStatus` subscription — saves one
   * Convex round-trip and one subscription handle. The parent already has
   * this subscribed and tick-aware (see `TournamentLobbyScreen`).
   */
  status?: TournamentStatus | null
  /**
   * Optional shared 1-second tick from the parent. Used for the run-time
   * display while the tournament is `active`. When omitted, this view runs
   * its own interval — used when the leaderboard is rendered standalone
   * (e.g. in tests).
   */
  now?: number
}

/**
 * Inline leaderboard content for the lobby tabs. Previously this lived in a
 * standalone `TournamentLeaderboardScreen` route; now it renders inside the
 * lobby's tab strip so navigation between Chat / Leaderboard / Tables /
 * Results doesn't push new routes.
 */
export function TournamentLeaderboardView({
  tournamentId,
  justEliminated,
  justFinished,
  status: statusProp,
  now: nowProp
}: Props) {
  const store = useStore()
  const { user } = store.session
  const { setEvent } = store.analytics
  const userId = user?.auth0Id || ''

  const myTable = useMyTournamentTable(tournamentId)
  // Skip the local subscription when the parent supplied `status`. Convex
  // client-side dedupes identical queries across components, but the local
  // subscription still costs a render trigger on every push — the prop
  // version inherits the parent's render cadence, which is what we want.
  const fallbackStatus = useQuery(
    api.tournaments.queries.getTournamentStatus,
    statusProp !== undefined ? 'skip' : { tournamentId }
  )
  const status = statusProp !== undefined ? statusProp : fallbackStatus
  const leaderboard = useQuery(api.tournaments.queries.getTournamentLeaderboard, { tournamentId })
  const leaderboardCounts = useQuery(api.tournaments.queries.getTournamentLeaderboardCounts, { tournamentId })

  const isEliminated = myTable?.tournamentEliminated === true
  const loading = status === undefined || leaderboard === undefined || leaderboardCounts === undefined
  const notFound = status === null || leaderboard === null || leaderboardCounts === null

  // Local row type that carries the server-assigned `rank` alongside the
  // shared `LeaderboardPlayer` fields. Renders use the rank value, not the
  // array index, because the server may inject the caller's elim row when
  // it falls outside the cap (creating gaps in the array indices).
  type LeaderboardRowEntry = LeaderboardPlayer & { rank: number }

  const leaderboardPlayers: LeaderboardRowEntry[] = useMemo(
    () =>
      (leaderboard ?? []).map((entry) => {
        const metaLabel = entry.isWinner ? 'Winner' : entry.tableNumber ? `Table ${entry.tableNumber}` : 'Eliminated'
        const stablePlayerId =
          entry.userId || `redacted:${entry.rank}:${entry.userName}:${entry.isEliminated ? 'out' : 'live'}`
        return {
          playerId: stablePlayerId,
          playerName: `${entry.userName}${entry.isBot ? ' (Bot)' : ''}`,
          chips: entry.chipCount,
          isLeft: entry.isEliminated,
          isRemovedByTeacher: false,
          metaLabel,
          rank: entry.rank
        }
      }),
    [leaderboard]
  )

  const myRank = useMemo(() => {
    const mine = leaderboard?.find((entry) => entry.userId === userId)
    return mine?.rank ?? 0
  }, [leaderboard, userId])

  // Key the color map on the JOINED user-ID list rather than the
  // `leaderboardPlayers` array reference itself. The reference churns on
  // every leaderboard push (ranks/chips change), but the set of player IDs
  // is usually stable across pushes until someone busts or late-joins.
  // Stabilizing the color refs lets `LeaderboardRow`'s `React.memo` shield
  // actually fire on rank-only updates.
  const playerIdsKey = useMemo(() => leaderboardPlayers.map((p) => p.playerId).join('|'), [leaderboardPlayers])
  const playerColorMap = useMemo(
    () => buildPlayerColorMap(leaderboardPlayers.map((p) => p.playerId)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [playerIdsKey]
  )

  const totalChips = status?.totalChips ?? 0

  // Prefer the parent-provided tick when available (lobby already ticks
  // once per second for the countdown); fall back to a local interval so
  // the view stays self-contained when rendered outside the lobby. Two
  // parallel 1-second intervals on the same tab was the original waste.
  const [fallbackNow, setFallbackNow] = useState(() => Date.now())
  const isActive = status?.state === 'active'
  useEffect(() => {
    if (!isActive || nowProp !== undefined) return
    const id = setInterval(() => setFallbackNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [isActive, nowProp])
  const now = nowProp ?? fallbackNow
  const runTime = useMemo(() => getRunTime(status, now), [status, now])

  const leaderboardViewedRef = useRef(false)
  useEffect(() => {
    if (leaderboardViewedRef.current) return
    if (!status || !leaderboard) return
    leaderboardViewedRef.current = true
    setEvent(events.mtt.leaderboard_screen_viewed, {
      tournamentId,
      state: status.state,
      isEliminated,
      myRank,
      playerCount: leaderboard.length
    })
  }, [status, leaderboard, isEliminated, myRank, tournamentId, setEvent])

  useEffect(() => {
    if (justEliminated) {
      toastNotificationStore.getState().show({
        text: 'You are out of the tournament. Final standings are below.'
      })
    }
  }, [justEliminated])

  if (loading) {
    return (
      <View style={styles.centered}>
        <DS.Text variant="Body Large/Regular" color="Brand/Dark Grey">
          Loading leaderboard…
        </DS.Text>
      </View>
    )
  }

  if (notFound) {
    return (
      <View style={styles.centered}>
        <DS.Text variant="Body Large/Regular" color="Brand/Dark Grey" style={{ textAlign: 'center' }}>
          Tournament leaderboard is not available.
        </DS.Text>
      </View>
    )
  }

  const numEntries = leaderboardCounts.activeReturned + leaderboardCounts.eliminatedReturned
  const totalEntries = leaderboardCounts.activeTotal + leaderboardCounts.eliminatedTotal
  const entriesLabel = leaderboardCounts.truncated ? `${totalEntries} total` : String(totalEntries)
  const showFinalTable = status.state === 'active' && status.activeTableCount === 1 && status.activePlayerCount > 1

  return (
    <ScrollView contentContainerStyle={styles.scrollContent} style={{ flex: 1 }}>
      <DS.Frame gap="16">
        <View style={styles.statsGrid}>
          <View style={styles.statsRow}>
            <Stat label="My Rank" value={myRank > 0 ? String(myRank) : '—'} highlight />
            <Stat label="Entries" value={entriesLabel} />
          </View>
          <View style={styles.statsRow}>
            <Stat label="Run Time" value={runTime} />
            <Stat label="Total Chips" value={totalChips.toLocaleString()} />
          </View>
        </View>

        {status.state === 'finished' && status.winnerName ? (
          <View style={styles.banner}>
            <DS.Text variant="Mini/Bold" color="Brand/Maroon" style={styles.eyebrow}>
              Winner
            </DS.Text>
            <DS.Text variant="Heading 3/Medium" color="Global/Black">
              {status.winnerName}
            </DS.Text>
          </View>
        ) : null}

        {status.state === 'active' ? (
          <View style={styles.banner}>
            <DS.Text variant="Mini/Bold" color="Brand/Maroon" style={styles.eyebrow}>
              {status.activePlayerCount} players remaining · {status.activeTableCount} table
              {status.activeTableCount !== 1 ? 's' : ''}
            </DS.Text>
            <DS.Text variant="Body Small/Medium" color="Global/Black">
              Blinds {status.currentSmallBlind}/{status.currentBigBlind}
              {status.nextBlindLevel && status.timeUntilNextBlind !== null
                ? ` · Next ${status.nextBlindLevel.smallBlind}/${status.nextBlindLevel.bigBlind} in ${Math.ceil(status.timeUntilNextBlind / 60000)}m`
                : ''}
            </DS.Text>
            {showFinalTable ? (
              <DS.Text variant="Mini/Bold" color="Brand/Maroon">
                Final Table
              </DS.Text>
            ) : null}
          </View>
        ) : null}

        {leaderboardCounts.truncated ? (
          <View style={styles.banner}>
            <DS.Text variant="Mini/Bold" color="Brand/Maroon" style={styles.eyebrow}>
              Partial Leaderboard
            </DS.Text>
            <DS.Text variant="Body Small/Medium" color="Global/Black">
              Showing {numEntries} of {totalEntries} entries. Your row is included when available.
            </DS.Text>
          </View>
        ) : null}

        <View style={styles.listCard}>
          <View style={styles.listHeader}>
            <Text style={[styles.listHeaderText, styles.listColRank]}>Rank</Text>
            <Text style={[styles.listHeaderText, styles.listColUser]}>User</Text>
            <Text style={[styles.listHeaderText, styles.listColChips]}>Chips</Text>
          </View>
          {leaderboardPlayers.map((player) => (
            // LinearTransition animates rows sliding to their new positions
            // when ranks change between leaderboard pushes. Keys are stable
            // per player (playerId), so only genuinely moved rows animate;
            // no `entering`/`exiting` prop means no animation on first render.
            <Animated.View key={player.playerId} layout={LinearTransition.duration(350)}>
              <LeaderboardRow
                player={player}
                isHighlighted={player.playerId === userId}
                color={playerColorMap.get(player.playerId)}
              />
            </Animated.View>
          ))}
        </View>
      </DS.Frame>
    </ScrollView>
  )
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <View style={[styles.statCell, highlight ? styles.statCellHighlight : null]}>
      <DS.Text variant="Mini/Bold" style={styles.eyebrow}>
        {label}
      </DS.Text>
      <DS.Text variant="Heading 3/Medium">{value}</DS.Text>
    </View>
  )
}

// Animated chip count: drives a Reanimated shared value toward the latest
// chip total and renders the interpolated number through a non-editable
// TextInput, the only stock RN component whose text can be driven from the UI
// thread. Same pattern as `screens/new-design/ReText.tsx`, with the
// leaderboard's typography applied.
const AnimatedTextInput = Animated.createAnimatedComponent(TextInput)

function AnimatedChipCount({ value }: { value: number }) {
  const displayed = useSharedValue(value)

  useEffect(() => {
    displayed.value = withTiming(value, { duration: 600, easing: Easing.out(Easing.cubic) })
  }, [value, displayed])

  const animatedProps = useAnimatedProps(() => ({
    text: Math.round(displayed.value).toLocaleString('en-US'),
    defaultValue: Math.round(displayed.value).toLocaleString('en-US')
  }))

  return (
    <AnimatedTextInput
      editable={false}
      underlineColorAndroid="transparent"
      animatedProps={animatedProps}
      style={styles.chipCount}
    />
  )
}

type LeaderboardRowProps = {
  player: LeaderboardPlayer & { rank: number }
  isHighlighted: boolean
  color: DSColor | undefined
}

/**
 * Custom equality function for `React.memo`. Shallow equality wouldn't help
 * because `leaderboardPlayers` is rebuilt as a new array on every leaderboard
 * Convex push (rank/chips change), so every `player` reference is fresh —
 * even when the visible fields are unchanged. Compare the rendered fields
 * directly to skip the row re-render when nothing visible changed.
 */
function leaderboardRowPropsEqual(prev: LeaderboardRowProps, next: LeaderboardRowProps): boolean {
  if (prev.isHighlighted !== next.isHighlighted) return false
  if (prev.color !== next.color) return false
  const a = prev.player
  const b = next.player
  return (
    a.playerId === b.playerId &&
    a.playerName === b.playerName &&
    a.chips === b.chips &&
    a.isLeft === b.isLeft &&
    a.isRemovedByTeacher === b.isRemovedByTeacher &&
    a.metaLabel === b.metaLabel &&
    a.rank === b.rank
  )
}

const LeaderboardRow = React.memo(function LeaderboardRow({ player, isHighlighted, color }: LeaderboardRowProps) {
  const statusSuffix = player.isRemovedByTeacher ? ' (Removed)' : ''
  return (
    <View style={[styles.row, isHighlighted ? styles.rowHighlighted : null]}>
      <View style={styles.listColRank}>
        <DS.Text variant="Body Medium/Regular">{player.rank}</DS.Text>
      </View>
      <View style={[styles.listColUser, styles.userCell]}>
        <PlayerAvatar name={player.playerName ?? ''} color={color ?? 'Brand/Maroon'} invert size={4} />
        <View style={styles.userText}>
          <Text
            style={[styles.userName, { textDecorationLine: player.isLeft ? 'line-through' : 'none' }]}
            numberOfLines={1}
          >
            {player.playerName}
            {statusSuffix}
          </Text>
          {player.metaLabel ? (
            <DS.Text variant="Mini/Regular" color="Brand/Dark Grey">
              {player.metaLabel}
            </DS.Text>
          ) : null}
        </View>
      </View>
      <View style={styles.listColChips}>
        <AnimatedChipCount value={player.chips ?? 0} />
      </View>
    </View>
  )
}, leaderboardRowPropsEqual)

const styles = StyleSheet.create({
  centered: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  // Tighter top padding so the stats grid sits close under the tab strip.
  scrollContent: { paddingHorizontal: 16, paddingTop: 8, paddingBottom: 16 },
  eyebrow: {
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: DS.palette['Brand/Maroon']
  },
  // Mirrors DS 'Body Medium/Medium' for the animated TextInput (which can't
  // consume DS.Text variants directly).
  chipCount: {
    fontFamily: fontFamily.GTAmericaStandardMedium,
    fontSize: 16,
    fontWeight: '500',
    letterSpacing: -0.25,
    color: DS.palette['Global/Black'],
    textAlign: 'right',
    padding: 0
  },
  statsGrid: { gap: 8 },
  statsRow: { flexDirection: 'row', gap: 8 },
  statCell: {
    flex: 1,
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 8,
    padding: 12,
    gap: 4,
    alignItems: 'flex-start'
  },
  statCellHighlight: {
    borderWidth: 1,
    borderColor: DS.palette['Brand/Maroon']
  },
  banner: {
    gap: 6,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: DS.palette['Global/White']
  },
  listCard: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 12,
    overflow: 'hidden'
  },
  listHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: DS.palette['Brand/Light Grey']
  },
  listHeaderText: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: DS.palette['Brand/Dark Grey']
  },
  listColRank: { width: 48 },
  listColUser: { flex: 1 },
  listColChips: { width: 100, textAlign: 'right' },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: DS.palette['Brand/Light Grey']
  },
  // Subtle "you are here" indicator: a maroon left edge plus a faint cream tint.
  // The row sits inside a white card; cream-on-white reads as a near-white wash
  // without screaming. The 3px left border anchors the eye when scanning long
  // lists. Reduce paddingLeft to compensate for the border so content alignment
  // matches non-highlighted rows.
  rowHighlighted: {
    backgroundColor: DS.palette['Brand/Cream'],
    borderLeftWidth: 3,
    borderLeftColor: DS.palette['Brand/Maroon'],
    paddingLeft: 9
  },
  userCell: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  userText: { flex: 1, gap: 2 },
  userName: { fontSize: 15, fontWeight: '400', color: DS.palette['Global/Black'] }
})
